import type { SupplierCertificate } from '@tandemone/types'
import { EVENTS } from '@tandemone/types'
import { useQueries } from '@tanstack/react-query'
import { ChevronDown, CircleCheck, Clock, Download } from 'lucide-react'
import { type ReactNode, useMemo, useState } from 'react'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from '@/components/ui/carousel'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { track } from '@/domains/observability'
import { SUPPLIER_CERTIFICATE_LABELS } from '@/domains/supplier-onboarding'
import { formatDate } from '@/lib/format-date'

import { CertBadge } from './cert-badge'
import { CertStatusBadge } from './cert-status-badge'
import {
  type DocumentDownloadResult,
  type PreviewDocument,
  DocumentPreviewDialog
} from './document-preview-dialog'
import { getFileIcon } from './file-icon'
import { PdfThumbnail } from './pdf-thumbnail'
import { ProfileEmpty } from './profile-empty'

/** the gallery renders any cert that carries a backing document, so only the
 * document-linked fields are required here. SupplierCertificate satisfies it,
 * as does the super review queue's UnverifiedCertificate mapped to verified
 * false.
 */
type CertItem = {
  documentId: string
  fileName: string
  contentType: string
  createdAt: string
  certification: SupplierCertificate['certification']
  verified: boolean
}

/** an optional table column threaded in by the caller. the super review page
 * uses it to show which company submitted each cert.
 */
export type CertExtraColumn = {
  header: string
  render: (certificateId: string) => ReactNode
}

/** identifiers the gallery needs to fire supplier_certificate_viewed when a
 * cert is opened. omitted by the cert-review queue and the company-self
 * Certificates page so neither inflates the insights count.
 */
export type CertificatesGalleryTracking = {
  supplierCompanyId: string
  supplierId: string
}

/** carousel of cert thumbnails + a table, both opening the scroll-through
 * preview dialog. each row shows the certification, its verified/pending
 * status, and (optionally) a caller-supplied column + trailing action.
 */
export function CertificatesGallery({
  certificates,
  fetchUrl,
  emptyState,
  extraColumn,
  rowAction,
  tracking,
  collapsible = true,
  defaultOpen = false
}: {
  certificates: SupplierCertificate[]
  fetchUrl: (documentId: string) => Promise<DocumentDownloadResult>
  emptyState?: ReactNode
  extraColumn?: CertExtraColumn
  rowAction?: (certificateId: string) => ReactNode
  tracking?: CertificatesGalleryTracking
  /** the detailed row list (mobile cards + desktop table) hides behind a
   *  toggle by default. callers whose whole job is reviewing every row (the
   *  super due-diligence packet, the cert-review queue) pass false to keep
   *  it always expanded, matching today's pre-ENG-61 behavior. */
  collapsible?: boolean
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  const items: (CertItem & { id: string })[] = useMemo(
    () =>
      certificates
        .filter(cert => cert.documentId !== null)
        .map(cert => ({
          id: cert.id,
          documentId: cert.documentId!,
          fileName: cert.documentFileName ?? 'Certificate',
          contentType: cert.documentContentType ?? 'application/octet-stream',
          createdAt: cert.createdAt,
          certification: cert.certification,
          verified: cert.verified
        }))
        .sort(
          (left, right) =>
            new Date(right.createdAt).getTime() -
            new Date(left.createdAt).getTime()
        ),
    [certificates]
  )

  const previewDocuments: PreviewDocument[] = useMemo(
    () =>
      items.map(item => ({
        id: item.documentId,
        fileName: item.fileName,
        contentType: item.contentType,
        category: 'certificate',
        createdAt: item.createdAt
      })),
    [items]
  )

  const [previewIndex, setPreviewIndex] = useState<number | null>(null)

  const previews = useQueries({
    queries: items.map(item => ({
      queryKey: ['document-preview', item.documentId],
      queryFn: () => fetchUrl(item.documentId),
      staleTime: 60_000
    }))
  })

  if (items.length === 0) {
    return (
      <>
        {emptyState ?? <ProfileEmpty message='No certifications uploaded.' />}
      </>
    )
  }

  function openPreview(index: number): void {
    setPreviewIndex(index)
    const item = items[index]
    if (tracking && item) {
      track(EVENTS.SUPPLIER_CERTIFICATE_VIEWED, {
        viewed_company_id: tracking.supplierCompanyId,
        viewed_supplier_id: tracking.supplierId,
        certificate_id: item.id,
        certification: item.certification
      })
    }
  }

  return (
    <>
      <div className='flex flex-col gap-5'>
        <Carousel opts={{ align: 'start', dragFree: true }} className='w-full'>
          <CarouselContent className='-ml-3'>
            {items.map((item, idx) => {
              const preview = previews[idx]
              const isImage = item.contentType.startsWith('image/')
              const isPdf = item.contentType === 'application/pdf'
              const { Icon, className: iconClassName } = getFileIcon(
                item.contentType
              )
              const iconFallback = (
                <div className='flex size-full flex-col items-center justify-center gap-2 p-2'>
                  <Icon className={`size-8 ${iconClassName}`} />
                  <span className='line-clamp-2 text-center text-[10px] text-muted-foreground'>
                    {SUPPLIER_CERTIFICATE_LABELS[item.certification]}
                  </span>
                </div>
              )
              return (
                <CarouselItem
                  key={item.id}
                  className='basis-1/2 pl-3 sm:basis-1/3 md:basis-1/4 lg:basis-1/5'
                >
                  <button
                    type='button'
                    onClick={() => openPreview(idx)}
                    className='group relative block aspect-square w-full cursor-pointer overflow-hidden rounded-md border border-border bg-muted'
                    aria-label={`Open ${SUPPLIER_CERTIFICATE_LABELS[item.certification]}`}
                  >
                    {isImage && preview?.data ? (
                      <img
                        src={preview.data.previewUrl}
                        alt={item.fileName}
                        loading='lazy'
                        className='size-full object-cover transition-transform group-hover:scale-105'
                      />
                    ) : isPdf && preview?.data ? (
                      <PdfThumbnail
                        documentId={item.documentId}
                        url={preview.data.previewUrl}
                        fileName={item.fileName}
                        className='transition-transform group-hover:scale-105'
                        fallback={iconFallback}
                      />
                    ) : (
                      iconFallback
                    )}
                    <CertBadge
                      certification={item.certification}
                      className='absolute top-1.5 left-1.5 max-w-[calc(100%-2.5rem)] truncate'
                    />
                    <span className='absolute right-1.5 top-1.5 grid size-6 place-items-center rounded-full bg-page/90 shadow-sm'>
                      {item.verified ? (
                        <CircleCheck className='size-4 text-emerald-600 dark:text-emerald-400' />
                      ) : (
                        <Clock className='size-4 text-amber-600 dark:text-amber-400' />
                      )}
                    </span>
                  </button>
                </CarouselItem>
              )
            })}
          </CarouselContent>
          {/* embla drags on touch, so on a phone the arrows would only sit
              over the end thumbnails. */}
          <CarouselPrevious className='left-2 hidden size-9 border-transparent bg-accent text-white shadow-sm hover:bg-accent/90 disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100 sm:inline-flex' />
          <CarouselNext className='right-2 hidden size-9 border-transparent bg-accent text-white shadow-sm hover:bg-accent/90 disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100 sm:inline-flex' />
        </Carousel>

        <Collapsible open={!collapsible || open} onOpenChange={setOpen}>
          {collapsible ? (
            <CollapsibleTrigger className='flex cursor-pointer items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground'>
              <ChevronDown
                className={`size-4 transition-transform ${open ? '' : '-rotate-90'}`}
              />
              {open ? 'Hide' : 'Show'} certifications
              <span className='text-xs font-normal'>({items.length})</span>
            </CollapsibleTrigger>
          ) : null}
          <CollapsibleContent className='flex flex-col gap-5 pt-3'>
            {/** mobile: stacked cards. table doesn't reflow below md so each cert
             * becomes its own card with title + meta + actions, mirroring the
             * column data without horizontal overflow.
             */}
            <div className='flex flex-col gap-3 md:hidden'>
              {items.map((item, idx) => {
                const { Icon, className: iconClassName } = getFileIcon(
                  item.contentType
                )
                const preview = previews[idx]
                return (
                  <div
                    key={item.id}
                    className='flex flex-col gap-3 rounded-lg border border-border bg-page p-4'
                  >
                    <div className='flex items-start justify-between gap-3'>
                      <button
                        type='button'
                        onClick={() => openPreview(idx)}
                        className='flex min-w-0 flex-1 cursor-pointer items-start gap-2 text-left'
                      >
                        <Icon
                          className={`mt-0.5 size-4 shrink-0 ${iconClassName}`}
                        />
                        <div className='min-w-0'>
                          <CertBadge
                            certification={item.certification}
                            className='max-w-full truncate'
                          />
                          <p className='truncate text-[11px] text-muted-foreground'>
                            {item.fileName}
                          </p>
                        </div>
                      </button>
                      <CertStatusBadge verified={item.verified} />
                    </div>

                    {/** meta + actions share one row so the card doesn't grow a
                     * dead vertical band between them. wraps onto two lines on
                     * narrow screens when both sides can't fit.
                     */}
                    <div className='flex flex-wrap items-center justify-between gap-x-3 gap-y-2'>
                      <div className='flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground'>
                        {extraColumn ? (
                          <>
                            <span className='text-foreground'>
                              {extraColumn.render(item.id)}
                            </span>
                            <span aria-hidden>·</span>
                          </>
                        ) : null}
                        <span>{formatDate(item.createdAt)}</span>
                      </div>
                      {preview?.data || rowAction ? (
                        <div className='flex w-full items-center justify-end gap-1.5 sm:w-auto'>
                          {preview?.data ? (
                            <a
                              href={preview.data.downloadUrl}
                              target='_blank'
                              rel='noopener noreferrer'
                              download
                              aria-label={`Download ${item.fileName}`}
                              className='inline-grid size-11 cursor-pointer place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground md:size-8'
                            >
                              <Download className='size-4' />
                            </a>
                          ) : null}
                          {rowAction ? rowAction(item.id) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>
                )
              })}
            </div>

            <Table className='hidden md:table'>
              <TableHeader>
                <TableRow>
                  <TableHead>Certificate</TableHead>
                  {extraColumn ? (
                    <TableHead>{extraColumn.header}</TableHead>
                  ) : null}
                  <TableHead>Status</TableHead>
                  <TableHead>Added</TableHead>
                  <TableHead
                    className={rowAction ? 'w-44 text-right' : 'w-12'}
                  />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item, idx) => {
                  const { Icon, className: iconClassName } = getFileIcon(
                    item.contentType
                  )
                  const preview = previews[idx]
                  return (
                    <TableRow
                      key={item.id}
                      onClick={() => openPreview(idx)}
                      className='cursor-pointer'
                    >
                      <TableCell className='max-w-[16rem]'>
                        <div className='inline-flex max-w-full items-center gap-2'>
                          <Icon
                            className={`size-4 shrink-0 ${iconClassName}`}
                          />
                          <CertBadge
                            certification={item.certification}
                            className='max-w-full truncate'
                          />
                        </div>
                        <p className='ml-6 mt-0.5 truncate text-[11px] text-muted-foreground'>
                          {item.fileName}
                        </p>
                      </TableCell>
                      {extraColumn ? (
                        <TableCell className='text-sm text-foreground'>
                          {extraColumn.render(item.id)}
                        </TableCell>
                      ) : null}
                      <TableCell>
                        <CertStatusBadge verified={item.verified} />
                      </TableCell>
                      <TableCell className='text-xs text-muted-foreground'>
                        {formatDate(item.createdAt)}
                      </TableCell>
                      <TableCell onClick={event => event.stopPropagation()}>
                        <div className='flex items-center justify-end gap-1.5'>
                          {preview?.data ? (
                            <a
                              href={preview.data.downloadUrl}
                              target='_blank'
                              rel='noopener noreferrer'
                              download
                              aria-label={`Download ${item.fileName}`}
                              className='inline-grid size-8 cursor-pointer place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
                            >
                              <Download className='size-4' />
                            </a>
                          ) : null}
                          {rowAction ? rowAction(item.id) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </CollapsibleContent>
        </Collapsible>
      </div>
      <DocumentPreviewDialog
        documents={previewDocuments}
        initialIndex={previewIndex}
        onClose={() => setPreviewIndex(null)}
        fetchUrl={fetchUrl}
      />
    </>
  )
}
